import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Switch,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';

const TABLE_HEAD = [
  { id: 'name', label: 'Nombre', alignRight: false },
  { id: 'description', label: 'Descripción', alignRight: false },
  { id: 'priceDisplay', label: 'Precio', alignRight: false },
  { id: 'subscriberCount', label: 'Suscriptores', alignRight: false },
  { id: 'active', label: 'Activo', alignRight: false },
  { id: '', label: '', alignRight: false },
];

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_item) => _item.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

const db = getFirestore(firebaseApp);

export default function MembershipsPage() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sortOrder');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [memberships, setMemberships] = useState([]);
  const [row, setRow] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const snap = await getDocs(collection(db, 'membership_plans'));
      setMemberships(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error('Error fetching memberships:', error);
    }
    setTableLoading(false);
  };

  const handleOpenMenu = (event, r) => { setOpen(event.currentTarget); setRow(r); };
  const handleCloseMenu = () => setOpen(null);

  const handleEditClick = () => { navigate(`information?id=${row.id}`); handleCloseMenu(); };

  const handleDeleteClick = async () => {
    if (!window.confirm(`¿Eliminar la membresía "${row.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'membership_plans', row.id));
      setMemberships(memberships.filter((m) => m.id !== row.id));
    } catch (error) {
      alert('Error eliminando membresía');
    }
    handleCloseMenu();
  };

  const handleToggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'membership_plans', item.id), { active: !item.active });
      setMemberships(memberships.map((m) => (m.id === item.id ? { ...m, active: !m.active } : m)));
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  const filteredList = applySortFilter(memberships, getComparator(order, orderBy), filterName);

  return (
    <>
      <Helmet>
        <title>Membresías | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>Membresías</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => navigate('information')}>
            Nueva Membresía
          </Button>
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={(e) => { setPage(0); setFilterName(e.target.value); }} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={(e, prop) => {
                    setOrder(orderBy === prop && order === 'asc' ? 'desc' : 'asc');
                    setOrderBy(prop);
                  }}
                />
                <TableBody>
                  {tableLoading && <TableSkeleton rows={3} columns={6} />}
                  {!tableLoading && filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                    <TableRow hover key={item.id} tabIndex={-1}>
                      <TableCell padding="checkbox" />
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>{item.name}</Typography>
                        {item.requiresEvidence && (
                          <Chip label="Requiere evidencia" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {item.description ? (
                          <Tooltip title={item.description} arrow placement="top">
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ cursor: 'default' }}>
                              {item.description}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatPrice(item.priceCents)}/{item.intervalMonths && item.intervalMonths !== 12 ? `${item.intervalMonths / 12} años` : 'año'}
                        </Typography>
                        {item.renewalPriceCents > 0 && (
                          <Typography variant="caption" color="success.main" display="block">
                            Renovación: {formatPrice(item.renewalPriceCents)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.subscriberCount || 0}
                          size="small"
                          color={item.subscriberCount > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Switch checked={item.active || false} onChange={() => handleToggleActive(item)} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, item)}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tableLoading && filteredList.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <Typography variant="body2">No hay membresías creadas</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={handleEditClick}>
          <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} /> Editar
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} /> Eliminar
        </MenuItem>
      </Popover>
    </>
  );
}
